import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { CalendarClock, Users, Gavel, Youtube, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const WORKSHOP_SLUG = "trial-strategy-workshop-tyler-robinson";

// Buckets, in display order: Special/Workshops (the hook) first, then Bullpens, then Court.
const GROUPS = [
  { key: "special", label: "Workshops & Special Events", icon: CalendarClock, blurb: "Interactive series and one-off deep dives." },
  { key: "bullpen", label: "The Bullpen", icon: Users, blurb: "Live members-only case reviews — recordings added to the Case Review Library." },
  { key: "court", label: "Court Dates & Premieres", icon: Gavel, blurb: "Key hearings, verdicts, and video premieres." },
];

function bucketOf(e) {
  if (e.case_slug === WORKSHOP_SLUG || e.type === "special") return "special";
  if (e.type === "meeting") return "bullpen";
  return "court";
}

function fmtDate(d, t) {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  const day = dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  return t ? `${day} · ${t} (Arizona MST)` : day;
}

function EventCard({ e, isMember, joinable, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      className="rounded-lg overflow-hidden border border-white/10 card-surface flex flex-col sm:flex-row hover:border-gold/40 transition-colors"
      data-testid={`event-card-${e.id}`}
    >
      <div className="relative w-full sm:w-64 lg:w-72 shrink-0 h-40 sm:h-auto">
        {e.image ? <img src={e.image} alt={e.title} className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-white/5 flex items-center justify-center"><CalendarClock className="w-10 h-10 text-gold/40" /></div>}
        <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-charcoal/80 sm:from-transparent sm:to-charcoal/60 to-transparent" />
      </div>
      <div className="p-6 flex-1 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="label-mono text-gold/90">{fmtDate(e.date, e.time)}</div>
          <h3 className="mt-1.5 font-display text-lg font-bold text-white leading-tight">{e.title}</h3>
          {e.description && <p className="mt-2 text-sm text-white/55 leading-relaxed line-clamp-2">{e.description}</p>}
        </div>
        <div className="shrink-0">
          {isMember ? (
            <Link to="/calendar" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:text-gold-hover transition-colors" data-testid={`event-details-${e.id}`}>
              {joinable ? "Details & join link" : "View details"} <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <Link to="/pricing" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm whitespace-nowrap" data-testid={`event-join-${e.id}`}>
              {joinable ? "Join to attend" : "Members get details"} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function EventsPage() {
  const { user } = useAuth();
  const isMember = !!user && (user.role === "admin" || user.subscription_status === "active");
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api.get("/public/events").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, []);

  const grouped = {};
  (events || []).forEach((e) => {
    const b = bucketOf(e);
    (grouped[b] = grouped[b] || []).push(e);
  });

  return (
    <div className="min-h-screen bg-charcoal">
      <Helmet><title>Events · Help Solve The Case</title></Helmet>
      <Navbar />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-12">
        <div className="label-mono text-gold flex items-center gap-2 mb-3"><CalendarClock className="w-4 h-4" /> What's coming up</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white">Events</h1>
        <p className="mt-4 text-white/60 max-w-2xl leading-relaxed">
          Live case reviews in The Bullpen, interactive workshops, and the court dates we&apos;re tracking together.
          Members get the join link inside their Calendar — <Link to="/pricing" className="text-gold hover:underline">join the case team</Link> to pull up a seat.
        </p>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 space-y-16">
        {events === null && <p className="text-white/40 label-mono">Loading events…</p>}
        {events !== null && events.length === 0 && (
          <div className="card-surface rounded-lg p-12 text-center border border-white/10">
            <CalendarClock className="w-10 h-10 text-gold/40 mx-auto mb-4" />
            <p className="text-white/60">No upcoming events scheduled yet — check back soon.</p>
          </div>
        )}
        {GROUPS.map((g) => {
          const list = grouped[g.key] || [];
          if (list.length === 0) return null;
          const Icon = g.icon;
          return (
            <section key={g.key} data-testid={`events-group-${g.key}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-sm bg-gold/15 border border-gold/30 flex items-center justify-center"><Icon className="w-5 h-5 text-gold" /></div>
                <div>
                  <h2 className="font-display text-2xl font-bold text-white">{g.label}</h2>
                  <p className="text-sm text-white/45">{g.blurb}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 mt-6">
                {list.map((e, i) => <EventCard key={e.id} e={e} isMember={isMember} joinable={g.key !== "court"} delay={i * 0.05} />)}
              </div>
            </section>
          );
        })}
      </div>

      <Footer />
    </div>
  );
}
